import { Dialog, DialogContent } from './ui/dialog';
import ModalCloseButton from './ModalCloseButton';
import { Sparkles, AlertTriangle, BadgeCheck, Coins, BookOpen, Building2, Hand } from 'lucide-react';

/**
 * DripExplainerModal — opens from the header link
 * "DRIP, what it means for your Portfolio."
 *
 * Scrollable rich-content modal with sections, tables, bullets and a
 * compact disclaimer at the bottom. Closes via:
 *   1) the standard ModalCloseButton (top-right X) — matches the
 *      animated close button used by every other modal in the app.
 *   2) clicking the dimmed backdrop (Radix default).
 *   3) Escape key.
 *
 * Static content — purely educational, no API calls.
 */
export default function DripExplainerModal({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] w-[calc(100vw-1rem)] overflow-y-auto overflow-x-hidden p-0 gap-0 [&>button[data-testid='dialog-close-btn']]:hidden"
        data-testid="drip-explainer-modal"
      >
        {/* Header — sticky so the title + close button stay visible
            while the user scrolls through the long content. We render
            our own ModalCloseButton inside the sticky bar because the
            DialogContent's built-in absolute-positioned close button
            ends up rendering BEHIND this sticky bar (the bar's
            bg-background covers the upper-right corner). Putting the
            button inside the bar guarantees it stays on top. */}
        <div className="sticky top-0 z-10 bg-background border-b px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 text-fuchsia-500 dark:text-fuchsia-400 shrink-0" />
            <h2 className="text-base sm:text-xl font-bold tracking-tight break-words leading-snug">
              DRIP — What It Means for Your Portfolio
            </h2>
          </div>
          <ModalCloseButton onClick={onClose} testId="drip-explainer-close" />
        </div>

        {/* Body */}
        <div className="px-5 sm:px-7 py-5 space-y-7 text-sm leading-relaxed [&_p]:break-words [&_li]:break-words">

          {/* What is a DRIP */}
          <section data-testid="drip-section-what">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-violet-500 dark:text-violet-400" />
              What Is a DRIP?
            </h3>
            <p className="text-foreground/90">
              <span className="font-semibold">DRIP</span> stands for{' '}
              <span className="font-semibold">Dividend Reinvestment Plan</span>. Instead of paying
              your dividends out as cash, the brokerage (or company) automatically uses that money
              to buy more shares — including fractional shares — of the same stock that paid them.
              Every dollar you earn goes right back to work growing your position.
            </p>
          </section>

          {/* Why investors use DRIPs */}
          <section data-testid="drip-section-why">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              Why Investors Use DRIPs
            </h3>
            <ul className="list-disc list-outside pl-5 space-y-1.5">
              <li><span className="font-semibold">Compounding growth.</span> Your new shares earn their own dividends, which then buy more shares. The famous "snowball effect."</li>
              <li><span className="font-semibold">Zero commissions.</span> Most brokerage and company DRIPs reinvest for free.</li>
              <li><span className="font-semibold">Fractional shares.</span> Every cent of your dividend gets invested, regardless of share price.</li>
              <li><span className="font-semibold">Share discounts.</span> Some company-sponsored plans offer 1–10% off the market price.</li>
              <li><span className="font-semibold">Dollar-cost averaging.</span> Auto-reinvestment at regular intervals means more shares when prices are low, fewer when they're high.</li>
            </ul>
          </section>

          {/* Important considerations */}
          <section data-testid="drip-section-considerations">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Important Considerations
            </h3>
            <ul className="list-disc list-outside pl-5 space-y-1.5">
              <li><span className="font-semibold">Taxes still apply</span> in taxable accounts. Even though you never touch the cash, the IRS treats reinvested dividends as taxable income for that year.</li>
              <li><span className="font-semibold">Over-concentration.</span> Auto-buying more of the same stock can leave a portfolio overweight in a single name.</li>
              <li><span className="font-semibold">No cash flow.</span> If you need the money to pay bills, DRIP isn't right — the cash is gone the moment it's paid.</li>
              <li><span className="font-semibold">Record-keeping.</span> In taxable accounts, every reinvestment creates a new tax lot, which makes cost-basis tracking more complex when you eventually sell.</li>
            </ul>
          </section>

          {/* How to set up */}
          <section data-testid="drip-section-howto">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              How to Set One Up
            </h3>
            <ul className="list-disc list-outside pl-5 space-y-1.5">
              <li><span className="font-semibold">Through a brokerage.</span> Schwab, Fidelity, Robinhood, etc. — toggle "Reinvest Dividends" on for the whole account or specific tickers. Usually one click.</li>
              <li><span className="font-semibold">Directly from the company.</span> Some big names (Coca-Cola, Johnson &amp; Johnson) run their own plans through a transfer agent. More paperwork, but sometimes a share discount.</li>
            </ul>
          </section>

          {/* Roth IRA */}
          <section data-testid="drip-section-roth">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <BadgeCheck className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
              DRIP + Roth IRA: A Tax-Free Compounding Machine
            </h3>
            <p className="mb-2.5">
              In a Roth IRA, a DRIP is essentially tax-free. Roth accounts are funded with
              <em> after-tax</em> dollars, so neither dividends nor capital gains generate any
              annual tax bill while the money grows inside the account.
            </p>
            <ul className="list-disc list-outside pl-5 space-y-1.5">
              <li><span className="font-semibold">No tax on dividends.</span> Reinvested shares aren't a taxable event.</li>
              <li><span className="font-semibold">No capital gains tax.</span> When you sell shares later, 100% of the profit is yours.</li>
              <li><span className="font-semibold">No cost-basis paperwork.</span> Tax lots don't matter — there's nothing to report.</li>
              <li><span className="font-semibold">Maximum compounding.</span> Nothing leaves the account, so every dollar of dividends keeps buying more shares.</li>
            </ul>
          </section>

          {/* Comparison Table */}
          <section data-testid="drip-section-table">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-amber-500" />
              Account-Type Comparison
            </h3>
            {/* Desktop / tablet (sm+) — proper 4-column table */}
            <div className="hidden sm:block rounded-lg border overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold w-[22%]">Feature</th>
                    <th className="text-left px-3 py-2 font-semibold">Taxable Brokerage</th>
                    <th className="text-left px-3 py-2 font-semibold">Traditional IRA</th>
                    <th className="text-left px-3 py-2 font-semibold">Roth IRA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-3 py-2 font-medium">Tax on Dividends</td>
                    <td className="px-3 py-2 text-muted-foreground">Paid annually (15–20%)</td>
                    <td className="px-3 py-2 text-muted-foreground">Deferred until withdrawal</td>
                    <td className="px-3 py-2 text-emerald-700 dark:text-emerald-400 font-semibold">None</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Tax on Growth</td>
                    <td className="px-3 py-2 text-muted-foreground">Paid when you sell</td>
                    <td className="px-3 py-2 text-muted-foreground">Deferred until withdrawal</td>
                    <td className="px-3 py-2 text-emerald-700 dark:text-emerald-400 font-semibold">None</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Withdrawals</td>
                    <td className="px-3 py-2 text-muted-foreground">Tax-free (already paid)</td>
                    <td className="px-3 py-2 text-muted-foreground">Taxed as ordinary income</td>
                    <td className="px-3 py-2 text-emerald-700 dark:text-emerald-400 font-semibold">Tax-free (after 59½)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">DRIP Utility</td>
                    <td className="px-3 py-2 text-muted-foreground">Good — but complicates taxes</td>
                    <td className="px-3 py-2 text-muted-foreground">Great for growth</td>
                    <td className="px-3 py-2 text-fuchsia-600 dark:text-fuchsia-400 font-semibold">Optimal</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile — stacked card per account type so a 4-column
                grid never has to fit in 360–390px of width. Each card
                lists the same 4 rows as label / value pairs. */}
            <div className="sm:hidden space-y-3">
              {[
                {
                  title: 'Taxable Brokerage',
                  tone: 'text-foreground',
                  rows: [
                    ['Tax on Dividends', 'Paid annually (15–20%)'],
                    ['Tax on Growth', 'Paid when you sell'],
                    ['Withdrawals', 'Tax-free (already paid)'],
                    ['DRIP Utility', 'Good — but complicates taxes'],
                  ],
                },
                {
                  title: 'Traditional IRA',
                  tone: 'text-foreground',
                  rows: [
                    ['Tax on Dividends', 'Deferred until withdrawal'],
                    ['Tax on Growth', 'Deferred until withdrawal'],
                    ['Withdrawals', 'Taxed as ordinary income'],
                    ['DRIP Utility', 'Great for growth'],
                  ],
                },
                {
                  title: 'Roth IRA',
                  tone: 'text-fuchsia-600 dark:text-fuchsia-400',
                  rows: [
                    ['Tax on Dividends', 'None'],
                    ['Tax on Growth', 'None'],
                    ['Withdrawals', 'Tax-free (after 59½)'],
                    ['DRIP Utility', 'Optimal'],
                  ],
                  highlight: true,
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className={`rounded-lg border p-3 ${card.highlight ? 'border-fuchsia-300/70 dark:border-fuchsia-500/40 bg-fuchsia-50/40 dark:bg-fuchsia-500/5' : 'bg-muted/20'}`}
                >
                  <div className={`text-sm font-bold mb-2 ${card.tone}`}>{card.title}</div>
                  <dl className="text-xs space-y-1.5">
                    {card.rows.map(([k, v]) => (
                      <div key={k} className="grid grid-cols-[40%_60%] gap-2">
                        <dt className="font-medium text-foreground/80">{k}</dt>
                        <dd className={`break-words ${card.highlight ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-muted-foreground'}`}>{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </section>

          {/* Roth Rules */}
          <section data-testid="drip-section-rules">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <span
                className="relative inline-flex items-center justify-center w-6 h-6 shrink-0"
                aria-hidden="true"
                data-testid="drip-rules-stop-icon"
              >
                {/* Red hexagon outline behind the hand — stop-sign motif */}
                <svg
                  viewBox="0 0 24 24"
                  className="absolute inset-0 w-full h-full text-red-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                >
                  <polygon points="6,2 18,2 22,12 18,22 6,22 2,12" />
                </svg>
                <Hand className="relative w-3.5 h-3.5 text-red-500" strokeWidth={2.25} />
              </span>
              The Two Roth IRA Rules to Stay Tax-Free
            </h3>
            <ul className="list-disc list-outside pl-5 space-y-1.5">
              <li><span className="font-semibold">5-Year Rule:</span> the account must be open at least five tax years before earnings can be withdrawn tax-free.</li>
              <li><span className="font-semibold">Age 59½:</span> withdrawals of earnings before this age generally hit a 10% penalty (with some exceptions).</li>
            </ul>
          </section>

          {/* Bottom line */}
          <section data-testid="drip-section-bottom">
            <h3 className="text-base font-bold mb-2 flex items-center gap-1.5">
              <img
                src="/dividrip-icon2.png"
                alt=""
                aria-hidden="true"
                className="h-5 w-auto object-contain shrink-0"
                data-testid="drip-bottom-line-icon"
              />
              The Bottom Line for DiviDrip Users
            </h3>
            <p>
              Every projection in this app assumes DRIP is on by default — that's the magic that
              turns a 4% yield into far more than a 4% return over 10 or 20 years. If your
              portfolio's <span className="font-semibold">Yield on Cost</span> climbs year after
              year, that's DRIP plus annual dividend hikes doing exactly what they're built to do.
              Use the DRIP calculator on any stock or your full portfolio to see the snowball in
              action.
            </p>
          </section>

          {/* Disclaimer */}
          <div className="rounded-lg border-2 border-red-400 dark:border-red-700 px-4 py-3 mt-2">
            <p className="text-[11px] font-medium leading-relaxed text-amber-900 dark:text-yellow-400">
              <span className="font-bold">Disclaimer:</span> This page is educational content only —
              not financial, tax, or legal advice. Tax rules change, brokerage policies vary, and
              every investor's situation is different. Always confirm specifics with a qualified
              financial professional before relying on any strategy described here.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
